// Constantes da aplicação
import { IS_CHECKLIST_RECOVERY_DIAGNOSTIC } from './recoveryDiagnostic';

const STRIPE_EDGE_FUNCTIONS = {
  createCheckout: { prod: 'createCheckout', dev: 'createCheckout-dev' },
  handleSuccess: { prod: 'handleSuccess', dev: 'handleSuccess-dev' },
  getSubscription: { prod: 'getSubscription', dev: 'getSubscription-dev' },
  cancelSubscription: { prod: 'cancelSubscription', dev: 'cancelSubscription-dev' },
  listPrices: { prod: 'listPrices', dev: 'listPrices-dev' },
  stripeWebhook: { prod: 'stripeWebhook', dev: 'stripeWebhook-dev' },
} as const;
const USE_PROD_STRIPE_EDGE_FUNCTIONS = true; // Defina false para usar as versões "-dev"

export const APP_CONSTANTS = {
  // Informações da aplicação
  APP_NAME: 'App Guincho Checklists',
  VERSION: '1.0.152',

  // URLs da aplicação
  URLS: {
    //PUBLIC_URL: 'http://localhost:8081', // URL base para web - ATENÇÃO: em produção, usar o domínio do site
    PUBLIC_URL: 'https://www.appguincho.com.br', // URL base para web - ATENÇÃO: em produção, usar o domínio do site
    RESET_PASSWORD_PATH: '/reset-password', // Caminho para a página de reset de senha
  },

  STORE_URLS: {
    ANDROID_MARKET_URL: 'market://details?id=com.mmaj.appguincho',
    ANDROID_WEB_URL: 'https://play.google.com/store/apps/details?id=com.mmaj.appguincho',
    IOS_APP_STORE_URL: 'https://apps.apple.com/app/id6760430732',
  },

  // Contatos da aplicação
  CONTACTS: {
    WHATSAPP_NUMBER: '5531983364526',
    WHATSAPP_URL: 'https://wa.me/5531983364526',
  },

  // Configurações de planos
  PLANS: {
    FREE: 'free',
    TRIAL: 'trial',
    PREMIUM: 'premium',
  },

  SUBSCRIPTION_TRIAL: {
    PLAN_ID:
      process.env.EXPO_PUBLIC_SUBSCRIPTION_TRIAL_PLAN_ID ||
      '3a9c7567-c091-4d13-8706-f4b0cd1fea65', // Atualize com o ID real em guincho_subscription_plans
    DURATION_DAYS: 3,
    EXTERNAL_ID_PREFIX: 'manual:trial-',
  },

  APPLE_IAP: {
    EXTERNAL_ID_PREFIX: 'manual:apple:',
    PLANS: [
      {
        tier: 5,
        productId: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_ID_5 || '',
        subscriptionPlanId: '5d2c7e8f-1a6b-4d3c-9e8f-7b2a6c5d4e3b',
      },
      {
        tier: 10,
        productId: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_ID_10 || '',
        subscriptionPlanId: '73ec26ae-172b-462b-a205-2cb3c9b950e9',
      },
      {
        tier: 20,
        productId: process.env.EXPO_PUBLIC_APPLE_IAP_PRODUCT_ID_20 || '',
        subscriptionPlanId: '051d074b-82c1-4113-bd65-25d90595a883',
      },
    ],
  },

  // Timeouts e intervalos (em ms)
  TIMEOUTS: {
    SYNC_INTERVAL: 180000, // 5 minutos (ajustável conforme estratégias de sync)
    NETWORK_TIMEOUT: 10000, // 10 segundos
    SUBSCRIPTION_REVALIDATION_INTERVAL: 120000, // 5 minutos para validar assinatura periodicamente
  },

  // Configurações de layout/responsividade
  UI: {
    DESKTOP_BREAKPOINT: 1024, // largura mínima para ativar layout desktop/web
  },

  // Configurações gerais de sincronização
  SYNC: {
    SUPABASE_PULL_PAGE_SIZE: 5000, // Registros por requisição ao Supabase durante o pull; deve acompanhar o Max Rows configurado no projeto
    LOCAL_BULK_UPSERT_THRESHOLD: 100, // A partir deste volume por página, prefere upsert local em lote durante bootstrap completo
  },

  // Limites
  LIMITS: {
    FREE_TIER: {
      MAX_ITEMS: 50,
      MAX_STORAGE: 100 * 1024 * 1024, // 100MB
    },
    PREMIUM_TIER: {
      MAX_ITEMS: -1, // ilimitado
      MAX_STORAGE: 10 * 1024 * 1024 * 1024, // 10GB
    },
  },

  // Endpoints de API (se necessário além do Supabase)
  API: {
    PAYMENT: {
      STRIPE: 'https://api.stripe.com/v1',
      PIX: '/api/payments/pix',
    },
  },

  // Configuração de cadastro
  REGISTRATION: {
    REQUIRES_COMPANY: true, // Define se o app requer cadastro de empresa
    USE_SIGNUP_REDIRECT_FLOW: false, // true = fluxo atual com redirect/email, false = fluxo direto
    DIRECT_SIGNUP_DEFAULTS: {
      COMPANY_NAME: 'Nome da sua Empresa',
      USER_NAME: 'Seu nome',
    },
  },

  // Flags para habilitar ou desabilitar funcionalidades específicas
  FEATURE_FLAGS: {
    SHOW_DEBUG_ACTIONS: false,
  },

  EDGE_FUNCTIONS: {
    STRIPE: {
      USE_PROD: USE_PROD_STRIPE_EDGE_FUNCTIONS,
      FUNCTIONS: STRIPE_EDGE_FUNCTIONS,
    },
  },

  // Configuração do Storage no Supabase
  STORAGE: {
    BUCKET: {
      COMPANY: 'app-guincho', // bucket para imagens de empresas
      USER: 'app-guincho', // bucket para imagens de usuários
      CHECKLIST: 'app-guincho', // bucket para imagens de checklists (mesmo bucket, pasta diferente)
    },
    FOLDER: {
      COMPANY_LOGO: 'logo-empresa',
      USER_AVATAR: 'user-photos',
      CHECKLIST_PHOTOS: 'checklists-photos', // pasta para fotos de checklists
      CHECKLIST_SIGNATURES: 'checklists-signatures', // pasta para assinaturas de checklists
      CHECKLIST_MARCACOES: 'checklists-marcacoes', // pasta para marcacoes de checklists
      CHECKLIST_MARCACAO_BKG: 'template-marcacoes', // pasta para backgrounds de marcacoes de checklists
    },
  },

  // Configuração do banco de dados local
  DATABASE: {
    // Definição das tabelas locais
    TABLES: {
      // Tabela para armazenar operações pendentes de sincronização
      PENDING_OPERATIONS: {
        NAME: 'pending_operations',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'operation', type: 'TEXT', notNull: true },
          { name: 'collection', type: 'TEXT', notNull: true },
          { name: 'item_id', type: 'TEXT', notNull: true },
          { name: 'data', type: 'TEXT' },
          { name: 'timestamp', type: 'INTEGER', notNull: true },
          { name: 'user_id', type: 'TEXT' },
          // Legados sem escopo permanecem nulos e não podem ser enviados.
          { name: 'empresa_id', type: 'TEXT' }
        ],
        INDEXES: [
          { name: 'idx_pending_operations', columns: ['timestamp'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Tabela de controle interno, não sincronizada'
        }
      },

      // Conflitos materializados localmente, fora da fila operacional de retry.
      SYNC_CONFLICTS: {
        NAME: 'sync_conflicts',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'user_id', type: 'TEXT' },
          { name: 'pending_operation_id', type: 'TEXT' },
          { name: 'collection', type: 'TEXT', notNull: true },
          { name: 'item_id', type: 'TEXT', notNull: true },
          { name: 'field_id', type: 'TEXT' },
          { name: 'snapshot_field_id', type: 'TEXT' },
          { name: 'reason', type: 'TEXT', notNull: true },
          { name: 'payload', type: 'TEXT' },
          { name: 'payload_sha512', type: 'TEXT' },
          { name: 'recovery_id', type: 'TEXT' },
          { name: 'status', type: 'TEXT', notNull: true, defaultValue: "'open'" },
          { name: 'created_at', type: 'INTEGER', notNull: true },
          { name: 'updated_at', type: 'INTEGER', notNull: true },
          { name: 'resolved_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_sync_conflicts_pending', columns: ['pending_operation_id'] },
          { name: 'idx_sync_conflicts_scope', columns: ['empresa_id', 'status'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Conflitos locais classificados; sincronizacao futura por RPC dedicada'
        }
      },

      // Feed local aplicado de exclusoes/aposentadorias duraveis.
      SYNC_TOMBSTONES: {
        NAME: 'sync_tombstones',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'entity_type', type: 'TEXT', notNull: true },
          { name: 'entity_id', type: 'TEXT', notNull: true },
          { name: 'parent_id', type: 'TEXT' },
          { name: 'sequence', type: 'INTEGER', notNull: true },
          { name: 'deletion_kind', type: 'TEXT', notNull: true },
          { name: 'metadata', type: 'TEXT' },
          { name: 'deleted_at', type: 'INTEGER', notNull: true },
          { name: 'applied_at', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_sync_tombstones_scope_sequence', columns: ['empresa_id', 'sequence'] },
          { name: 'idx_sync_tombstones_entity', columns: ['empresa_id', 'entity_type', 'entity_id'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Espelho local do feed de tombstones obtido por RPC'
        }
      },

      // Cursor monotonicamente crescente por empresa e stream.
      SYNC_CURSORS: {
        NAME: 'sync_cursors',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'stream', type: 'TEXT', notNull: true },
          { name: 'sequence', type: 'INTEGER', notNull: true, defaultValue: 0 },
          { name: 'updated_at', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_sync_cursors_scope', columns: ['empresa_id', 'stream'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Cursores locais por empresa; nao apagados por pull incremental'
        }
      },

      // Evidencia duravel retirada da fila ativa somente apos classificacao terminal.
      ACCOUNTED_PENDING_OPERATIONS: {
        NAME: 'accounted_pending_operations',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'user_id', type: 'TEXT' },
          { name: 'operation', type: 'TEXT', notNull: true },
          { name: 'collection', type: 'TEXT', notNull: true },
          { name: 'item_id', type: 'TEXT', notNull: true },
          { name: 'data', type: 'TEXT' },
          { name: 'original_timestamp', type: 'INTEGER', notNull: true },
          { name: 'accounting_state', type: 'TEXT', notNull: true },
          { name: 'reason', type: 'TEXT' },
          { name: 'receipt_id', type: 'TEXT' },
          { name: 'payload_sha512', type: 'TEXT' },
          { name: 'accounted_at', type: 'INTEGER', notNull: true },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_accounted_pending_scope', columns: ['empresa_id', 'accounted_at'] },
          { name: 'idx_accounted_pending_item', columns: ['collection', 'item_id'] },
          { name: 'idx_accounted_pending_state', columns: ['accounting_state'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Arquivo local imutavel das pending_operations contabilizadas'
        }
      },

      // Tabela de empresas
      EMPRESAS: {
        NAME: 'guincho_empresas',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'nome', type: 'TEXT', notNull: true },
          { name: 'cnpj', type: 'TEXT' },
          { name: 'email', type: 'TEXT' },
          { name: 'telefone', type: 'TEXT' },
          { name: 'endereco', type: 'TEXT' },
          { name: 'cep', type: 'TEXT' },
          { name: 'cidade', type: 'TEXT' },
          { name: 'uf', type: 'TEXT' },
          { name: 'logo_url', type: 'TEXT' },
          { name: 'logo_local_path', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' },
          { name: 'plano', type: 'TEXT' },
          { name: 'stripe_customer_id', type: 'TEXT' },
          { name: 'num_fotos', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_empresas_nome', columns: ['nome'] }
        ],
        SYNC_CONFIG: {
          enabled: true, // Sincroniza a tabela de empresas
          filterByUser: false, // Não filtra por usuário
          filterByCompany: true, // Filtra por empresa
          forceCompanyFilter: false, // Não força o filtro por empresa
          companyField: 'id', // Campo de empresa
          description: 'Sincroniza apenas a empresa do usuário atual'
        }
      },

      // Tabela de usuários
      USERS: {
        NAME: 'guincho_usuarios',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'email', type: 'TEXT', notNull: true },
          { name: 'name', type: 'TEXT' },
          { name: 'password', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' },
          { name: 'photo_url', type: 'TEXT' },
          { name: 'photo_local_path', type: 'TEXT' },
          { name: 'telefone', type: 'TEXT' },
          { name: 'empresa_id', type: 'TEXT' },
          { name: 'user_type', type: 'TEXT', defaultValue: "'motorista'" }
        ],
        INDEXES: [
          { name: 'idx_users_email', columns: ['email'] },
          { name: 'idx_users_empresa', columns: ['empresa_id'] }
        ],
        FOREIGN_KEYS: [
          { column: 'empresa_id', reference: 'guincho_empresas(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: false,
          userField: 'id',
          companyField: 'empresa_id',
          description: 'Sincroniza o usuário atual e/ou usuários da mesma empresa'
        }
      },

      // Tabela de planos de assinaturas
      SUBSCRIPTION_PLANS: {
        NAME: 'guincho_subscription_plans',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'name', type: 'TEXT', notNull: true },
          { name: 'description', type: 'TEXT' },
          { name: 'price', type: 'NUMERIC' },
          { name: 'billing_cycle', type: 'TEXT' },
          { name: 'features', type: 'TEXT' }, // JSON armazenado como texto
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' },
          { name: 'external_price_id', type: 'TEXT' } // ID do preço no Stripe
        ],
        INDEXES: [
          { name: 'idx_plans_name', columns: ['name'] }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: false,
          description: 'Sincroniza todos os planos de assinatura disponíveis'
        }
      },

      // Tabela de assinaturas
      SUBSCRIPTIONS: {
        NAME: 'guincho_subscriptions',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'subscription_plan_id', type: 'TEXT', notNull: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'start_date', type: 'INTEGER', notNull: true }, // data armazenada como timestamp
          { name: 'end_date', type: 'INTEGER' },
          { name: 'status', type: 'TEXT', notNull: true },
          { name: 'next_billing_date', type: 'INTEGER' },
          { name: 'external_subscription_id', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_subscriptions_empresa', columns: ['empresa_id'] },
          { name: 'idx_subscriptions_plan', columns: ['subscription_plan_id'] },
          { name: 'idx_subscriptions_status', columns: ['status'] }
        ],
        FOREIGN_KEYS: [
          { column: 'subscription_plan_id', reference: 'guincho_subscription_plans(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza apenas assinaturas da empresa do usuário atual'
        }
      },

      // Tabela de pagamentos
      PAYMENTS: {
        NAME: 'guincho_payments',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'subscription_id', type: 'TEXT', notNull: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'amount', type: 'NUMERIC', notNull: true },
          { name: 'payment_date', type: 'INTEGER' },
          { name: 'status', type: 'TEXT', notNull: true },
          { name: 'method', type: 'TEXT', notNull: true },
          { name: 'transaction_reference', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' },
          { name: 'external_subscription_id', type: 'TEXT' }
        ],
        INDEXES: [
          { name: 'idx_payments_subscription', columns: ['subscription_id'] },
          { name: 'idx_payments_empresa', columns: ['empresa_id'] },
          { name: 'idx_payments_status', columns: ['status'] }
        ],
        FOREIGN_KEYS: [
          { column: 'subscription_id', reference: 'guincho_subscriptions(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza apenas pagamentos da empresa do usuário atual'
        }
      },

      // Tabela para armazenar pendências de imagens
      PENDING_IMAGES: {
        NAME: 'pending_images',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'item_type', type: 'TEXT', notNull: true },
          { name: 'item_id', type: 'TEXT', notNull: true },
          { name: 'local_uri', type: 'TEXT', notNull: true },
          { name: 'bucket', type: 'TEXT', notNull: true },
          { name: 'path', type: 'TEXT', notNull: true },
          { name: 'column_name', type: 'TEXT' },
          { name: 'campo_id', type: 'TEXT' },
          // Nulos preservam pendências legadas; elas não são elegíveis para envio.
          { name: 'user_id', type: 'TEXT' },
          { name: 'empresa_id', type: 'TEXT' },
          { name: 'baseline_captured', type: 'INTEGER' },
          { name: 'expected_partner_revision', type: 'INTEGER' },
          { name: 'expected_remote_value', type: 'TEXT' },
          { name: 'expected_remote_updated_at', type: 'TEXT' },
          { name: 'prepared_public_url', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER', notNull: true },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_pending_images_item', columns: ['item_type', 'item_id'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Tabela de controle interno, não sincronizada'
        }
      },

      LOCAL_DATABASE_CONTROL: {
        NAME: 'local_database_control',
        COLUMNS: [{ name: 'id', type: 'TEXT', primaryKey: true }, { name: 'epoch', type: 'INTEGER', notNull: true }],
        INDEXES: [],
        SYNC_CONFIG: { enabled: false },
      },
      DEFERRED_MEDIA_CLEANUP: {
        NAME: 'deferred_media_cleanup',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'user_id', type: 'TEXT', notNull: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'checklist_id', type: 'TEXT', notNull: true },
          { name: 'field_id', type: 'TEXT' },
          { name: 'payload', type: 'TEXT', notNull: true },
          { name: 'created_at', type: 'INTEGER', notNull: true },
        ],
        INDEXES: [{ name: 'idx_deferred_media_cleanup_parent', columns: ['checklist_id'] }],
        SYNC_CONFIG: { enabled: false },
      },
      // Tabela para armazenar pendências de deleção de imagens
      PENDING_DELETIONS: {
        NAME: 'pending_deletions',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'type', type: 'TEXT', notNull: true }, // 'LOCAL' ou 'REMOTE'
          { name: 'path', type: 'TEXT', notNull: true },
          { name: 'itemType', type: 'TEXT', notNull: true }, // 'USER' ou 'COMPANY'
          { name: 'itemId', type: 'TEXT', notNull: true },
          { name: 'campo_id', type: 'TEXT' },
          // Nulos preservam pendências legadas; elas não são elegíveis para envio.
          { name: 'user_id', type: 'TEXT' },
          { name: 'empresa_id', type: 'TEXT' },
          { name: 'createdAt', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_pending_deletions_item', columns: ['itemType', 'itemId'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Tabela de controle interno, não sincronizada'
        }
      },

      TEMPLATES_CHECKLIST: {
        NAME: 'guincho_templates_checklist',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'nome', type: 'TEXT', notNull: true },
          { name: 'versao', type: 'INTEGER', notNull: true },
          { name: 'retired_at', type: 'INTEGER' },
          { name: 'retired_by', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_templates_checklist_empresa', columns: ['empresa_id'] },
          { name: 'idx_templates_checklist_nome', columns: ['nome'] }
        ],
        FOREIGN_KEYS: [
          { column: 'empresa_id', reference: 'guincho_empresas(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza apenas templates da empresa do usuário atual'
        }
      },

      GUINCHO_CAMPOS_TEMPLATE: {
        NAME: 'guincho_campos_template',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'versao', type: 'INTEGER', notNull: true },
          { name: 'grupo', type: 'TEXT' },
          { name: 'nome', type: 'TEXT', notNull: true },
          { name: 'tipo', type: 'TEXT', notNull: true },
          { name: 'endereco_gps', type: 'INTEGER' },
          { name: 'obrigatorio', type: 'INTEGER', notNull: true, defaultValue: 0 }, // booleano (0/1)
          { name: 'deletavel', type: 'INTEGER', notNull: true, defaultValue: 1 }, // booleano (0/1)
          { name: 'alteravel', type: 'INTEGER', notNull: true, defaultValue: 1 }, // booleano (0/1)
          { name: 'visivel', type: 'INTEGER', notNull: true, defaultValue: 1 }, // booleano (0/1)
          { name: 'ordem', type: 'INTEGER' },
          { name: 'retired_at', type: 'INTEGER' },
          { name: 'retired_by', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_campos_template_template', columns: ['template_id'] },
          { name: 'idx_campos_template_empresa', columns: ['empresa_id'] },
          { name: 'idx_campos_template_grupo', columns: ['grupo'] },
          { name: 'idx_campos_template_nome', columns: ['nome'] }
        ],
        FOREIGN_KEYS: [
          { column: 'template_id', reference: 'guincho_templates_checklist(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza todos os campos de templates de checklist'
        }
      },

      GUINCHO_DROPDOWN: {
        NAME: 'guincho_dropdown',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'tipo', type: 'TEXT', notNull: true },
          { name: 'valor', type: 'TEXT', notNull: true },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_dropdown_tipo', columns: ['tipo'] },
          { name: 'idx_dropdown_valor', columns: ['valor'] }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: false,
          description: 'Sincroniza todos os valores de dropdown de checklist'
        }
      },

      GUINCHO_MARCACAO_IMAGENS: {
        NAME: 'guincho_marcacao_imagens',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'campo_id', type: 'TEXT', notNull: true },
          { name: 'foto_url', type: 'TEXT', notNull: true },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_marcacao_imagens_template', columns: ['template_id'] },
          { name: 'idx_marcacao_imagens_empresa', columns: ['empresa_id'] },
          { name: 'idx_marcacao_imagens_campo', columns: ['campo_id'] }
        ],
        FOREIGN_KEYS: [
          { column: 'template_id', reference: 'guincho_templates_checklist(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' },
          { column: 'campo_id', reference: 'guincho_campos_template(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza todas as imagens de marcação de checklist'
        }
      },

      GUINCHO_CHECKLISTS: {
        NAME: 'guincho_checklists',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'versao', type: 'INTEGER', notNull: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'usuario_id', type: 'TEXT', notNull: false },
          { name: 'data_criacao', type: 'INTEGER', notNull: true },
          { name: 'status', type: 'TEXT', notNull: true },
          { name: 'snapshot_id', type: 'TEXT' },
          { name: 'retired_at', type: 'INTEGER' },
          { name: 'retired_by', type: 'TEXT' },
          { name: 'sequencial_numero', type: 'INTEGER' },
          { name: 'sequencial_codigo', type: 'TEXT' },
          { name: 'data_conclusao', type: 'INTEGER' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_checklists_template', columns: ['template_id'] },
          { name: 'idx_checklists_empresa', columns: ['empresa_id'] },
          { name: 'idx_checklists_usuario', columns: ['usuario_id'] },
          { name: 'idx_checklists_status', columns: ['status'] }
        ],
        FOREIGN_KEYS: [
          { column: 'template_id', reference: 'guincho_templates_checklist(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' },
          { column: 'usuario_id', reference: 'guincho_usuarios(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza todos os checklists da empresa do usuário atual'
        }
      },

      // Tabela para armazenar fotos de checklist
      CHECKLIST_PHOTOS: {
        NAME: 'checklist_photos',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'base64', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Armazena fotos de checklist em base64 no IndexedDB (web only)'
        }
      },

      // tabela para armazenar imagens de marcação de checklist
      CHECKLIST_MARCACOES: {
        NAME: 'checklist_marcacoes',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'base64', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Armazena marcacoes de checklist em base64 no IndexedDB (web only)'
        }
      },

      // Tabela para armazenar assinaturas de checklist
      CHECKLIST_SIGNATURES: {
        NAME: 'checklist_signatures',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'base64', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Armazena assinaturas de checklist em base64 no IndexedDB (web only)'
        }
      },

      // Tabela para armazenar imagens de background de marcações específicas
      CHECKLIST_MARCACAO_BKG_IMAGES: {
        NAME: 'checklist_marcacao_bkg_images',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'base64', type: 'TEXT' },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'campo_id', type: 'TEXT', notNull: true },
          { name: 'foto_url', type: 'TEXT', notNull: true },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Armazena imagens de background de marcações da tabela guincho_marcacao_imagens em base64 no IndexedDB (web only)'
        }
      },

      GUINCHO_VALORES_CHECKLIST: {
        NAME: 'guincho_valores_checklist',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true }, // UUID
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'checklist_id', type: 'TEXT', notNull: true },
          { name: 'campo_id', type: 'TEXT', notNull: false },
          { name: 'snapshot_field_id', type: 'TEXT', notNull: false },
          { name: 'valor', type: 'TEXT' },
          { name: 'observacao', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' }
        ],
        INDEXES: [
          { name: 'idx_valores_checklist_checklist', columns: ['checklist_id'] },
          { name: 'idx_valores_checklist_empresa', columns: ['empresa_id'] },
          { name: 'idx_valores_checklist_campo', columns: ['campo_id'] },
          { name: 'idx_valores_checklist_snapshot_field', columns: ['snapshot_field_id'] }
        ],
        FOREIGN_KEYS: [
          { column: 'checklist_id', reference: 'guincho_checklists(id)' },
          { column: 'empresa_id', reference: 'guincho_empresas(id)' },
          { column: 'campo_id', reference: 'guincho_campos_template(id)' }
        ],
        SYNC_CONFIG: {
          enabled: true,
          filterByUser: false,
          filterByCompany: true,
          forceCompanyFilter: true,
          companyField: 'empresa_id',
          description: 'Sincroniza todos os valores de campos de checklists'
        }
      },

      GUINCHO_TEMPLATE_SCHEMA_VERSIONS: {
        NAME: 'guincho_template_schema_versions',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'version', type: 'INTEGER', notNull: true },
          { name: 'schema_payload', type: 'TEXT', notNull: true },
          { name: 'schema_hash', type: 'TEXT', notNull: true },
          { name: 'source', type: 'TEXT', notNull: true },
          { name: 'created_by', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_template_schema_versions_scope', columns: ['empresa_id', 'template_id', 'version'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Catalogo historico imutavel; integracao futura por fluxo v2'
        }
      },

      GUINCHO_CHECKLIST_SNAPSHOTS: {
        NAME: 'guincho_checklist_snapshots',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'checklist_id', type: 'TEXT', notNull: true },
          { name: 'template_id', type: 'TEXT', notNull: true },
          { name: 'template_schema_version_id', type: 'TEXT', notNull: true },
          { name: 'schema_payload', type: 'TEXT', notNull: true },
          { name: 'schema_hash', type: 'TEXT', notNull: true },
          { name: 'source', type: 'TEXT', notNull: true },
          { name: 'created_by', type: 'TEXT' },
          { name: 'created_at', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_checklist_snapshots_scope', columns: ['empresa_id', 'checklist_id'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Snapshot historico imutavel; integracao futura por fluxo v2'
        }
      },

      GUINCHO_CHECKLIST_SNAPSHOT_FIELDS: {
        NAME: 'guincho_checklist_snapshot_fields',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'empresa_id', type: 'TEXT', notNull: true },
          { name: 'snapshot_id', type: 'TEXT', notNull: true },
          { name: 'original_campo_id', type: 'TEXT' },
          { name: 'field_key', type: 'TEXT', notNull: true },
          { name: 'grupo', type: 'TEXT' },
          { name: 'nome', type: 'TEXT', notNull: true },
          { name: 'tipo', type: 'TEXT', notNull: true },
          { name: 'ordem', type: 'INTEGER' },
          { name: 'obrigatorio', type: 'INTEGER', notNull: true, defaultValue: 0 },
          { name: 'visivel', type: 'INTEGER', notNull: true, defaultValue: 1 },
          { name: 'config', type: 'TEXT', notNull: true, defaultValue: "'{}'" },
          { name: 'created_at', type: 'INTEGER', notNull: true }
        ],
        INDEXES: [
          { name: 'idx_snapshot_fields_scope', columns: ['empresa_id', 'snapshot_id'] },
          { name: 'idx_snapshot_fields_original', columns: ['original_campo_id'] }
        ],
        SYNC_CONFIG: {
          enabled: false,
          description: 'Campos imutaveis dos snapshots; integracao futura por fluxo v2'
        }
      },

      // Tabela para armazenar logs enviados pelo buffer circular de checklists
      CHECKLIST_LOGS: {
        NAME: 'checklist_logs',
        COLUMNS: [
          { name: 'id', type: 'TEXT', primaryKey: true },
          { name: 'session_id', type: 'TEXT', notNull: true },
          { name: 'trigger', type: 'TEXT', notNull: true },
          { name: 'user_id', type: 'TEXT' },
          { name: 'empresa_id', type: 'TEXT' },
          { name: 'checklist_id', type: 'TEXT' },
          { name: 'context', type: 'TEXT' }, // JSONB no Supabase
          { name: 'device_info', type: 'TEXT' }, // JSONB no Supabase
          { name: 'logs', type: 'TEXT' }, // JSONB no Supabase
          { name: 'created_at', type: 'INTEGER' },
          { name: 'updated_at', type: 'INTEGER' },
        ],
        INDEXES: [
          { name: 'idx_checklist_logs_session_created_at', columns: ['session_id', 'created_at'] },
          { name: 'idx_checklist_logs_checklist', columns: ['checklist_id'] },
        ],
        SYNC_CONFIG: {
          enabled: true,
          pushEnabled: true,
          pullEnabled: false,
          filterByCompany: true,
          companyField: 'empresa_id',
          description: 'Logs enviados ao Supabase, processados apenas em push'
        }
      },
    }
  },

  // Configurações de debug
  DEBUG: {
    // Habilitar/desabilitar todos os logs
    ENABLED: !IS_CHECKLIST_RECOVERY_DIAGNOSTIC,

    // Definir nível de log global (1: error, 2: warn, 3: info, 4: debug, 5: verbose)
    LOG_LEVEL: 5,

    // Configurações do buffer circular de logs
    LOG_BUFFER: {
      ENABLED: !IS_CHECKLIST_RECOVERY_DIAGNOSTIC, // O APK de recuperacao nao captura dados sensiveis em buffer
      MAX_EVENTS: 4000,    // Fonte única do limite circular de diagnóstico
      MAX_PACKAGE_BYTES: 10 * 1024 * 1024,
      MAX_PERSISTED_BYTES: 2 * 1024 * 1024, // Cópia local; preserva margem para outros dados no web
      MAX_MESSAGE_LENGTH: 4000, // Comprimento máximo por mensagem antes de truncar
    },

    // Configurações por módulo
    MODULES: {
      SUPABASE: {
        ENABLED: false,        // Logs de autenticação
        LOGIN: true,          // Logs específicos da página de login
        SIGNUP: true,         // Logs específicos da página de cadastro
        FORGOT_PASSWORD: true, // Logs específicos da página de recuperação de senha
        SESSION: true,        // Logs relacionados à gestão de sessão
      },
      INITIALIZE: {
        ENABLED: true,        // Logs do processo de inicialização
        APP: true,            // Logs específicos da inicialização do app
      },
      NETWORK: {
        ENABLED: true,        // Logs de rede
        STATUS: true,         // Logs de status de conectividade
        EVENTS: true,         // Logs de eventos de rede
      },
      DATABASE: {
        ENABLED: true,        // Logs do banco de dados
        ADAPTER: true,        // Logs específicos dos adaptadores
        SERVICE: true,        // Logs específicos do serviço de banco
        SQLITE: true,         // Logs específicos do SQLite
        DEXIE: true,          // Logs específicos do Dexie
        QUERY: true,          // Logs de consultas
        TRANSACTION: true,    // Logs de transações
        SYNC: true,           // Logs de sincronização com o banco
      },
      'SQLITE-ADAPTER': {
        ENABLED: true,             // Habilitar todos os logs do SQLiteAdapter
        INITIALIZE: true,          // Logs de inicialização
        CREATE_TABLES: true,       // Logs de criação de tabelas
        EXECUTE_QUERY: true,       // Logs de execução de queries
        BULK_UPSERT: true,         // Logs agregados de upsert em lote
        CREATE_ITEM: true,         // Logs de criação de itens
        READ_ITEM: true,           // Logs de leitura de itens
        UPDATE_ITEM: true,         // Logs de atualização de itens
        DELETE_ITEM: true,         // Logs de deleção de itens
        LIST_ITEMS: true,          // Logs de listagem de itens
        REGISTER_PENDING_OP: true, // Logs de registro de operações pendentes
        GET_PENDING_OPS: true,     // Logs de obtenção de operações pendentes
        REMOVE_PENDING_OP: true,   // Logs de remoção de operações pendentes
        CLOSE: true,               // Logs de fechamento da conexão
      },
      'DEXIE-ADAPTER': {
        ENABLED: true,             // Habilitar todos os logs do DexieAdapter
        INITIALIZE: true,          // Logs de inicialização
        CREATE_TABLES: true,       // Logs de criação de tabelas
        BULK_UPSERT: true,         // Logs agregados de upsert em lote
        CREATE_ITEM: true,         // Logs de criação de itens
        READ_ITEM: true,           // Logs de leitura de itens
        UPDATE_ITEM: true,         // Logs de atualização de itens
        DELETE_ITEM: true,         // Logs de deleção de itens
        LIST_ITEMS: true,          // Logs de listagem de itens
        REGISTER_PENDING_OP: true, // Logs de registro de operações pendentes
        GET_PENDING_OPS: true,     // Logs de obtenção de operações pendentes
        REMOVE_PENDING_OP: true,   // Logs de remoção de operações pendentes
        CLOSE: true,               // Logs de fechamento da conexão
      },
      SYNC: {
        ENABLED: true,             // Habilitar todos os logs de sincronização
        INITIALIZE: true,          // Logs de inicialização do serviço
        SHUTDOWN: true,            // Logs de desativação do serviço
        PREREQUISITES: true,       // Logs de verificação de pré-requisitos
        MONITOR: true,             // Logs de monitoramento de pré-requisitos
        PUSH: true,                // Logs de envio de alterações locais
        PULL: true,                // Logs de obtenção de alterações remotas
        RETRY: true,               // Logs de tentativas de resincronização
        ERROR: true,               // Logs de erros durante a sincronização
        STATUS: true,              // Logs de status da sincronização
        CONFLICT: true,            // Logs de resolução de conflitos
        PROGRESS: true,            // Logs de progresso da sincronização
        MAIN: true,                // Logs gerais da sincronização (syncAll)          // Logs de operações específicas
        'OP-CREATE': true,            // Logs de operações de criação
        'OP-UPDATE': true,            // Logs de operações de atualização
        'OP-DELETE': true,            // Logs de operações de deleção
        'OP-CONFIG': true,            // Logs de configuração de tabelas
        'OP-CHECKLIST': true,            // Logs de operações de checklist
      },
      REALTIME: {
        ENABLED: true,             // Habilitar todos os logs de realtime
        SETUP: true,               // Logs de configuração de subscrições
        CLEANUP: true,             // Logs de limpeza de subscrições
        EVENTS: true,              // Logs de eventos recebidos
        ERROR: true,               // Logs de erros em subscrições
        RECONNECT: true,           // Logs de reconexão após falha
      },
      STATE: {
        ENABLED: true,        // Logs de estado global
        GLOBAL: true,         // Logs específicos do estado global
        USER: true,           // Logs específicos do estado do usuário
        'SYNC-MAIN': true,           // Logs gerais de sincronização
        'SYNC-PREREQUISITES': true,  // Logs de verificação de pré-requisitos
        'SYNC-PUSH': true,           // Logs de envio de dados para o servidor
        'SYNC-PULL': true,           // Logs de recebimento de dados do servidor
        'SYNC-STATUS': true,         // Logs de status de sincronização
        'SYNC-ERROR': true,          // Logs de erros de sincronização
        'SYNC-RETRY': true,          // Logs de tentativas de resincronização
        'REALTIME-MAIN': true,       // Logs gerais de realtime
        'REALTIME-SETUP': true,      // Logs de configuração de inscrições
        'REALTIME-EVENTS': true,     // Logs de eventos recebidos
        'REALTIME-CLEANUP': true,    // Logs de limpeza de inscrições
        'REALTIME-ERROR': true,      // Logs de erros em inscrições
        'COMPUTED-STATE': true,      // Logs de estado computado
      },
      'APP-LAYOUT': {
        ENABLED: true,        // Logs do arquivo _layout.tsx
        INIT: true,           // Logs de inicialização do layout
        RENDER: true,         // Logs de renderização
      },
      'TOAST': {
        ENABLED: true,        // Logs do arquivo toast.ts
      },
      'ID-CHECKLIST': {
        ENABLED: true,        // Logs do arquivo [id].tsx
      },
      'USE-CHECKLIST-EDITOR': {
        ENABLED: true,        // Logs do arquivo useChecklistEditor.ts
      },
      'PDF-SERVICE-WEB': {
        ENABLED: true,        // Logs do arquivo checklistPdfService.web.ts
      },
      'PDF-DOC-BUILDER': {
        ENABLED: true,        // Logs do arquivo checklistPdfDoc.ts
      },
      'CHECKLIST-ACTIONS-HELPER': {
        ENABLED: true,        // Logs do arquivo checklistActionsHelper.ts
      },
      'ADMIN': {
        ENABLED: true,        // Logs do arquivo _layout.tsx
        DASHBOARD: true,      // Logs do arquivo dashboard.tsx
        CHECKLISTS: true,     // Logs do arquivo [id].tsx
        TABLE: true,          // Logs do arquivo ChecklistsTable.web.tsx
        LIST: true,           // Logs do arquivo ChecklistsList.web.tsx
        KPI: true,            // Logs do arquivo ChecklistsKPI.web.tsx
        FILTER: true,         // Logs do arquivo ChecklistsFilter.web.tsx
      },
      'CHECKLIST-FIELDS-FORM': {
        ENABLED: true,        // Logs do arquivo ChecklistFieldsForm.tsx
        VERBOSE: false,       // Modo detalhado (usar apenas para depuração pesada)
      },
      'CHECKLIST-MARK-FIELD': {
        ENABLED: true,        // Logs do arquivo ChecklistMarkField.tsx
      },
      'CHECKLIST-PHOTO-FIELD': {
        ENABLED: true,        // Logs do arquivo ChecklistPhotoField.tsx
      },
      'PENDING-IMAGE-PICKER-RECOVERY': {
        ENABLED: true,        // Logs da recuperação de resultado pendente do ImagePicker no Android
      },
      'CHECKLIST-DROPDOWN-FIELD': {
        ENABLED: true,        // Logs do arquivo ChecklistDropdownField.tsx
      },
      'CHECKLIST-CEP-FIELD': {
        ENABLED: true,        // Logs do arquivo ChecklistCEPField.tsx
      },
      'CHECKLIST-IMAGE-SERVICE': {
        ENABLED: true,        // Logs do arquivo ChecklistImageService.ts
      },
      'CHECKLIST-IMAGE-CLEANUP-HELPER': {
        ENABLED: true,        // Logs do arquivo ChecklistImageCleanupHelper.ts
      },
      'CHECKLIST-TEMPLATE-CLEANUP-HELPER': {
        ENABLED: true,        // Logs do arquivo ChecklistTemplateCleanupHelper.ts
      },
      'APP-(APP)-LAYOUT': {
        ENABLED: true,        // Logs do arquivo _layout.tsx
        INIT: true,           // Logs de inicialização do layout
        RENDER: true,         // Logs de renderização
      },
      'APP-INDEX': {
        ENABLED: true,        // Logs do arquivo index.tsx
        INIT: true,           // Logs de inicialização da página inicial
        RENDER: true,         // Logs de renderização
        USER_STATE: true,     // Logs do estado do usuário na página inicial
      },
      'APP-(APP)-INDEX': {
        ENABLED: true,        // Logs do arquivo index.tsx
        INIT: true,           // Logs de inicialização da página inicial
        RENDER: true,         // Logs de renderização
        USER_STATE: true,     // Logs do estado do usuário na página inicial
        SUBSCRIBE: true,      // Logs relacionados à assinatura/upgrade
        CHECKLIST: true,      // Logs relacionados a checklists
      },
      'APP-(AUTH)-LAYOUT': {
        ENABLED: true,        // Logs do arquivo _layout.tsx
        INIT: true,           // Logs de inicialização do layout
        RENDER: true,         // Logs de renderização
      },
      'APP-(AUTH)-LOGIN': {
        ENABLED: true,        // Logs do arquivo login.tsx
        LOGIN: true,          // Logs de login
        REGISTER: true,       // Logs de registro
        USER_CHECK: true,     // Logs de verificação de usuário
        MAIN: true,           // Logs do arquivo login.tsx
      },
      'AUTH-LINKS': {
        ENABLED: true,        // Logs do arquivo authLinks.ts
        GET_URL: true,        // Logs de obtenção de URL
        SETUP_DEEP_LINKS: true, // Logs de configuração de deep links
        OPEN_BROWSER: true,   // Logs de abertura de navegador
      },
      'CHECKLIST-SERVICE': {
        ENABLED: true,        // Logs do arquivo checklistService.ts
        MAIN: true,           // Logs do arquivo checklistService.ts
      },
      'MARCACAO-IMAGE-HELPER': {
        ENABLED: true,        // Logs do arquivo marcacaoImageHelper.ts
        MAIN: true,           // Logs do arquivo marcacaoImageHelper.ts
      },
      'APP-(AUTH)-SIGNUP': {
        ENABLED: true,        // Logs do arquivo signup.tsx
        SIGNUP: true,          // Logs de signup
        USER_CHECK: true,     // Logs de verificação de usuário
        MAIN: true,           // Logs do arquivo signup.tsx
      },
      'AUTH-SERVICES': {
        ENABLED: true,        // Logs do arquivo authService.ts
        LOGIN: true,          // Logs de login
        REGISTER: true,       // Logs de registro
        USER_CHECK: true,     // Logs de verificação de usuário
        MAIN: true,           // Logs do arquivo authService.ts
      },
      'DATABASE-SERVICE': {
        ENABLED: true,             // Habilitar todos os logs do DatabaseService
        INITIALIZE: true,          // Logs do método initialize
        BULK_UPSERT: true,         // Logs do método bulkUpsert genérico
        CREATE_ITEM: true,         // Logs do método createItem
        READ_ITEM: true,           // Logs do método readItem
        UPDATE_ITEM: true,         // Logs do método updateItem
        DELETE_ITEM: true,         // Logs do método deleteItem
        LIST_ITEMS: false,          // Logs do método listItems
        GET_PENDING_OPS: true,     // Logs do método getPendingOperations
        REMOVE_PENDING_OP: true,   // Logs do método removePendingOperation
        CLOSE: true,               // Logs do método close
        VALIDATE_INIT: true,       // Logs do método validateInitialized
        BULK_UPSERT_CHECKLIST_VALUES: true, // Logs do método bulkUpsertChecklistValues
      },
      'SUBSCRIPTION-SERVICE': {
        ENABLED: true,             // Habilitar todos os logs do serviço de assinatura
        MAIN: true,                // Logs gerais do serviço
        STATUS: true,              // Logs para checkSubscriptionStatus
        PLANS: true,               // Logs para getAvailablePlans  
        PAYMENTS: true,            // Logs para getUserPayments
        SUBSCRIBE: true,           // Logs para subscribeToPlan
        LOCAL_DB: true,            // Logs para operações de banco local
        REMOTE_DB: true,           // Logs para operações remotas no Supabase
      },
      'USER-SERVICE': {
        ENABLED: true,        // Logs do serviço de usuário
        PROFILE: true,        // Logs para operações de perfil
        UPDATE: true,         // Logs para atualizações de perfil
        GET_USER_PROFILE: true, // Logs para getUserProfile
        GET_USER_COMPANY: true, // Logs para getUserCompany
        UPDATE_USER_PROFILE: true, // Logs para updateUserProfile
        UPLOAD_USER_PROFILE_IMAGE: true, // Logs para uploadUserProfileImage
      },
      'UPLOAD-IMAGE-HELPER': {
        ENABLED: true,        // Logs para operações de upload de imagem
        UPLOAD: true,         // Logs para upload de imagem
        DELETE: true,         // Logs para delete de imagem
        UTILS: true,         // Logs para utils de imagem
      },
      'FILE-HELPER': {
        ENABLED: true,        // Logs para operações de arquivo
        FETCH_FILE: true,     // Logs para fetchFile
        DELETE_LOCAL_FILE: true, // Logs para deleteLocalFile
      },
      'PROFILE': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
        INFO: true,
        EDIT: true,
        PLANS: true,
        PAYMENTS: true,
      },
      'APP-(APP)-SETTINGS-LAYOUT': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-INDEX': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-SUPPORT': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-SUBSCRIPTION': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
        PLANS: true,
        PAYMENTS: true,
        DETAILS: true,
      },
      'APP-(APP)-SETTINGS-PAYMENTS': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-COMPANY': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-ADVANCED': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-ACCOUNT': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-SETTINGS-ADD-USERS': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'APP-(APP)-PAINEL-DEV': {
        ENABLED: true,        // Logs do painel de desenvolvimento
        MAIN: true,
        DATA: true,           // Logs de carregamento/paginacao de dados
      },
      'APP-(APP)-PAINEL-DEV-EXPLORAR': {
        ENABLED: true,        // Logs da rota de exploracao de checklists
        MAIN: true,
        DATA: true,
      },
      'APP-(APP)-PAINEL-DEV-CHECKLIST': {
        ENABLED: true,        // Logs do editor online de checklist no painel dev
        MAIN: true,
        DATA: true,
        SAVE: true,
        IMAGE: true,
      },
      'APP-(APP)-SETTINGS-CHECKLISTS-TEMPLATES': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      'FORMATTED-INPUT': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
        NUMBER: true,
      },
      'FIELD-ORDERING': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
        BOARD: true,
      },
      'APP-(APP)-SETTINGS-CHECKLISTS': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
        ORDERING: true,
        FIELDS: true,
      },
      'CAMPO-MARCACAO-BACKGROUND-BUTTON': {
        ENABLED: true,        // Logs para operações de perfil
        MAIN: true,
      },
      // Flags de trace de imagens (simples e removível)
      // Ative para inspecionar alterações de URIs/URLs e eventos de upload
      // em tempo real no console via [IMG_TRACE]
    }
  ,
  // Flags de debug globais adicionais
  IMAGE_TRACE: true,
  SHOW_IMAGE_SOURCE: true
  }
};

export type StripeEdgeFunctionKey = keyof typeof STRIPE_EDGE_FUNCTIONS;

/**
 * Resolve o nome correto da Edge Function Stripe considerando o ambiente atual.
 */
export const getStripeEdgeFunctionName = (key: StripeEdgeFunctionKey): string =>
  USE_PROD_STRIPE_EDGE_FUNCTIONS
    ? STRIPE_EDGE_FUNCTIONS[key].prod
    : STRIPE_EDGE_FUNCTIONS[key].dev;

export default APP_CONSTANTS; 
